<template>
  <div
    class="tw:shrink-0"
    :class="{ active: active }"
  >
    <slot name="beforeTags" />

    <template v-if="hideInput">
      <span :class="errorClass">{{ hideInputLabel }}</span>
    </template>

    <template v-else>
      <div
        ref="inputWrapper"
        class="tw:relative"
      >
        <BaseInputText
          v-model="query"
          data-testid="query"
          :debounce="300"
          :disabled="disabled"
          new-design
          :placeholder="getLocaleText('global.search')"
          @click="visible = true"
          @keydown.enter.prevent
        />
        <Popper
          v-if="filteredBookmarkStreams.length && visible"
          v-model="visible"
          placement="bottom-start"
          :root-element="inputWrapper"
          same-width
        >
          <div
            ref="bookmarkList"
            class="c-dropdown-menu tw:max-h-[260px] tw:overflow-auto tw:bg-white"
          >
            <div class="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:py-2">
              <div v-if="!query">
                <BaseCheckbox
                  v-model="selectAll"
                  class="tw:font-bold"
                  dense
                  :indeterminate="isIndeterminate"
                  :label="$t('expandable_filters.all_tags')"
                  new-design
                  @update:model-value="toggleSelectAll"
                />
              </div>
              <div
                v-for="(bookmarkStream, index) in filteredBookmarkStreams"
                :key="index"
              >
                <BaseCheckbox
                  v-model="bookmarkStream.selected"
                  :data-testid="`tag-option-${bookmarkStream.id}`"
                  dense
                  :label="bookmarkStream.label"
                  new-design
                  @update:model-value="
                    updateSelectedBookmarkStreams(bookmarkStream)
                  "
                >
                  <template #label>
                    <BaseTag
                      :color="labelColor(bookmarkStream)"
                      hide-remove-button
                      :label="bookmarkStream.label"
                      :palette="palette"
                      rounded
                    />
                  </template>
                </BaseCheckbox>
              </div>
            </div>
          </div>
        </Popper>
      </div>

      <div
        v-if="tags.length"
        class="tw:mt-2 tw:flex tw:flex-wrap tw:gap-1"
        data-testid="tags"
      >
        <BaseTag
          v-for="bookmarkStream in tags"
          :key="bookmarkStream.label"
          :color="labelColor(bookmarkStream)"
          :data-testid="`tag-${bookmarkStream.id}`"
          hide-cursor
          :label="bookmarkStream.label"
          :palette="palette"
          rounded
          @remove="removeBookmarksStream(bookmarkStream)"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseInputText, BaseTag } from "shared/components/base";
import Popper from "shared/components/base/BasePopper";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import { defaultBookmarkPalette, tagPalette } from "shared/constants";
import { isSocialStream } from "shared/helpers/stream";
import type { StreamFiltersTag } from "shared/helpers/StreamFilters";
import { useStreamsStore } from "shared/stores/streams";
import type { FilterSet, Stream } from "shared/types";

export interface DropdownTagsProps {
  active?: boolean;
  disabled?: boolean;
  errorClass?: string;
  filterSet?: FilterSet;
  hideInput?: boolean;
  hideInputLabel?: string;
  stream?: Stream;
}

const tags = defineModel<StreamFiltersTag[]>("tags", {
  default: () => [],
});

const props = withDefaults(defineProps<DropdownTagsProps>(), {
  hideInputLabel: "",
  errorClass: "",
  filterSet: undefined,
  stream: undefined,
});

const inputWrapper = ref<HTMLDivElement>();
const bookmarkList = ref<HTMLDivElement | null>(null);
const query = ref<string>("");
const selectAll = ref<boolean>(false);
const visible = ref<boolean>(false);

const streamsStore = useStreamsStore();

const { mediaBookmarkStreams, socialBookmarkStreams } =
  storeToRefs(streamsStore);

const palette = computed(() => ({
  ...tagPalette,
  ...defaultBookmarkPalette,
}));

const emit = defineEmits<{
  search: [];
}>();

onMounted(() => {
  onClickOutside(bookmarkList, () => {
    visible.value = false;
  });
});

const bookmarkStreams = computed<Stream[]>(() => {
  if (props.stream) {
    if (isSocialStream(props.stream)) {
      return socialBookmarkStreams.value;
    }

    return mediaBookmarkStreams.value;
  }

  // filter sets can be used for both media and social streams
  if (props.filterSet) {
    return [...mediaBookmarkStreams.value, ...socialBookmarkStreams.value];
  }

  return [];
});

const filteredBookmarkStreams = computed<StreamFiltersTag[]>(() =>
  bookmarkStreams.value.reduce((acc: StreamFiltersTag[], stream) => {
    if (stream.label.toLowerCase().includes(query.value.toLowerCase())) {
      acc.push({
        id: stream.id,
        label: stream.label,
        color: stream.color,
        selected: tags.value.some((tag) => tag.id === stream.id),
      });
    }

    return acc;
  }, [])
);

const labelColor = (bookmark: StreamFiltersTag) =>
  bookmark.color || Object.keys(defaultBookmarkPalette)[0];

const addBookmarkStream = (bookmarkStream: StreamFiltersTag) => {
  tags.value = [...tags.value, bookmarkStream];

  emit("search");
};

const isIndeterminate = computed(
  () => tags.value.length > 0 && !selectAll.value
);

const removeBookmarksStream = (bookmarkStream: StreamFiltersTag) => {
  tags.value = tags.value.filter(
    (selectedBookmarkStream) => bookmarkStream.id !== selectedBookmarkStream.id
  );

  emit("search");
};

const updateSelectedBookmarkStreams = (bookmarkStream: StreamFiltersTag) => {
  const existingTag = tags.value.find(
    (selectedBookmarkStream) => selectedBookmarkStream.id === bookmarkStream.id
  );

  if (existingTag) {
    removeBookmarksStream(bookmarkStream);
  } else {
    addBookmarkStream(bookmarkStream);
  }
};

const toggleSelectAll = () => {
  let selectedBookmarksStreams: StreamFiltersTag[] = [];

  if (selectAll.value) {
    selectedBookmarksStreams = bookmarkStreams.value.map(
      (bookmark: StreamFiltersTag) => ({
        id: bookmark.id,
        label: bookmark.label,
        color: bookmark.color,
      })
    );
  }

  tags.value = selectedBookmarksStreams;
  emit("search");
};

function resetFilter() {
  if (visible.value) return;

  query.value = "";
}

function updateSelectAll() {
  selectAll.value = tags.value.length === bookmarkStreams.value.length;
}

watch(visible, () => resetFilter());

watch(
  () => tags.value,
  () => updateSelectAll()
);
</script>
